// ============================================================
// FONADHOO CRM v3.0 - EVENT MANAGEMENT COMPONENT
// Create events, track attendance, measure ROI
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import { EVENT_TYPES } from './shared/constants';
import { formatDate, daysUntil } from './shared/utils';
import SearchableSelect from './shared/SearchableSelect';
import { Icon } from './shared/Icon';

const BLANK_FORM = {
  name: '', type: 'Rally / Public Meeting', date: '', time: '',
  island: '', location: '', expected_attendance: '', cost: '', notes: ''
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: '6px',
  border: '1px solid var(--border-2)', background: 'var(--surface-2)',
  color: 'var(--text)', fontSize: '14px'
};

export default function EventManagement({ supabase, profile, user, allLocations = [], allVoters = [] }) {
  profile = profile ?? user;

  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterView, setFilterView] = useState('All');
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(BLANK_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(BLANK_FORM);
  const [pendingId, setPendingId] = useState(null);
  const [attendanceId, setAttendanceId] = useState(null);
  const [attendanceValue, setAttendanceValue] = useState('');
  const [openAttendeesId, setOpenAttendeesId] = useState(null);      // event whose attendee panel is open
  const [attendeesByEvent, setAttendeesByEvent] = useState({});      // eventId -> [{voter_id, support_before}]
  const [attendeeSearch, setAttendeeSearch] = useState('');
  const [attendeePending, setAttendeePending] = useState(false);
  const voterById = useMemo(() => new Map((allVoters || []).map(v => [v.id, v])), [allVoters]);
  const [expandedIslands, setExpandedIslands] = useState(() => new Set());
  const toggleIsland = (key) => setExpandedIslands(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  useEffect(() => { loadEvents(); }, [filterView]);

  const loadEvents = async () => {
    setLoading(true);
    const today = new Date().toISOString().split('T')[0];

    let query = supabase.from('events').select('*');
    if (filterView === 'Upcoming') query = query.gte('date', today).neq('completed', true).order('date', { ascending: true });
    else if (filterView === 'Overdue') query = query.lt('date', today).neq('completed', true).order('date', { ascending: false });
    else if (filterView === 'Completed') query = query.eq('completed', true).order('date', { ascending: false });
    else                               query = query.order('date', { ascending: false });

    const [{ data, error }, { data: all, error: allErr }] = await Promise.all([
      query,
      supabase.from('events').select('id, date, actual_attendance, cost, completed')
    ]);
    if (error) console.error('[events] filtered load failed:', error);
    if (allErr) console.error('[events] full load failed:', allErr);

    setEvents(data || []);
    setAllEvents(all || []);
    setLoading(false);
  };

  // Stats always from full table
  const today = new Date().toISOString().split('T')[0];
  const stats = {
    total:      allEvents.length,
    upcoming:   allEvents.filter(e => e.date >= today && !e.completed).length,
    overdue:    allEvents.filter(e => e.date < today && !e.completed).length,
    attendance: allEvents.reduce((s, e) => s + (e.actual_attendance || 0), 0),
    cost:       allEvents.reduce((s, e) => s + (e.cost || 0), 0),
  };

  const handleAddEvent = async () => {
    if (!addForm.name || !addForm.date) {
      alert('Please enter event name and date');
      return;
    }
    const { error } = await supabase.from('events').insert({
      ...addForm,
      expected_attendance: parseInt(addForm.expected_attendance) || 0,
      cost: parseFloat(addForm.cost) || 0,
      created_by: profile?.id
    });
    if (error) { alert('Error: ' + error.message); return; }
    setShowAdd(false);
    setAddForm(BLANK_FORM);
    loadEvents();
  };

  const handleSaveEdit = async (eventId) => {
    if (!editForm.name || !editForm.date) {
      alert('Please enter event name and date');
      return;
    }
    setPendingId(eventId);
    const { error } = await supabase.from('events').update({
      ...editForm,
      expected_attendance: parseInt(editForm.expected_attendance) || 0,
      cost: parseFloat(editForm.cost) || 0,
    }).eq('id', eventId);

    if (error) {
      setPendingId(null);
      alert('Save failed: ' + error.message);
      return;
    }

    const { data: row } = await supabase.from('events').select('name, date').eq('id', eventId).single();
    setPendingId(null);

    if (row?.name === editForm.name && row?.date === editForm.date) {
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, ...editForm } : e));
      setEditingId(null);
    } else {
      alert(
        'Could not save — Supabase RLS is blocking UPDATE.\n\n' +
        'Run in Supabase SQL Editor:\n' +
        'DROP POLICY IF EXISTS "update_events" ON events;\n' +
        'CREATE POLICY "update_events" ON events FOR UPDATE TO authenticated USING (true) WITH CHECK (true);'
      );
    }
  };

  const handleCompleteEvent = async (eventId) => {
    setPendingId(eventId);

    const { error } = await supabase.from('events').update({ completed: true }).eq('id', eventId);
    if (error) {
      setPendingId(null);
      alert('Complete failed: ' + error.message);
      return;
    }

    const { data: row } = await supabase.from('events').select('completed').eq('id', eventId).single();
    setPendingId(null);

    if (row?.completed) {
      setAllEvents(prev => prev.map(e => e.id === eventId ? { ...e, completed: true } : e));
      // Reload so a completed event leaves the Upcoming filter immediately.
      loadEvents();
    } else {
      alert(
        'Could not complete — Supabase RLS is blocking UPDATE.\n\n' +
        'Run in Supabase SQL Editor:\n' +
        'CREATE POLICY "update_events" ON events FOR UPDATE TO authenticated USING (true);'
      );
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Delete this event?')) return;
    setPendingId(eventId);

    const { error } = await supabase.from('events').delete().eq('id', eventId);
    if (error) {
      setPendingId(null);
      alert('Delete failed: ' + error.message);
      return;
    }

    const { data: row } = await supabase.from('events').select('id').eq('id', eventId).single();
    setPendingId(null);

    if (!row) {
      setEvents(prev => prev.filter(e => e.id !== eventId));
      setAllEvents(prev => prev.filter(e => e.id !== eventId));
    } else {
      alert(
        'Could not delete — Supabase RLS is blocking DELETE.\n\n' +
        'Run in Supabase SQL Editor:\n' +
        'CREATE POLICY "delete_events" ON events FOR DELETE TO authenticated USING (true);'
      );
    }
  };

  const handleSaveAttendance = async (eventId) => {
    const val = parseInt(attendanceValue);
    if (isNaN(val) || val < 0) { alert('Enter a valid number'); return; }
    setPendingId(eventId);

    const { error } = await supabase.from('events').update({ actual_attendance: val }).eq('id', eventId);
    if (error) {
      setPendingId(null);
      alert('Error: ' + error.message);
      return;
    }

    setPendingId(null);
    setEvents(prev => prev.map(e => e.id === eventId ? { ...e, actual_attendance: val } : e));
    setAllEvents(prev => prev.map(e => e.id === eventId ? { ...e, actual_attendance: val } : e));
    setAttendanceId(null);
    setAttendanceValue('');
  };

  // ── Attendee check-in (event_attendance table) ──────────────
  const loadAttendees = async (eventId) => {
    const { data } = await supabase.from('event_attendance')
      .select('voter_id, support_before, checked_in_time').eq('event_id', eventId);
    setAttendeesByEvent(prev => ({ ...prev, [eventId]: data || [] }));
  };
  const toggleAttendees = (eventId) => {
    if (openAttendeesId === eventId) { setOpenAttendeesId(null); return; }
    setOpenAttendeesId(eventId);
    setAttendeeSearch('');
    if (!attendeesByEvent[eventId]) loadAttendees(eventId);
  };
  // Keep the headline actual_attendance in sync with the check-in count.
  const syncCount = async (eventId, n) => {
    await supabase.from('events').update({ actual_attendance: n }).eq('id', eventId);
    setEvents(prev => prev.map(e => e.id === eventId ? { ...e, actual_attendance: n } : e));
    setAllEvents(prev => prev.map(e => e.id === eventId ? { ...e, actual_attendance: n } : e));
  };
  const checkInVoter = async (eventId, voter) => {
    const list = attendeesByEvent[eventId] || [];
    if (list.find(a => a.voter_id === voter.id)) return;
    setAttendeePending(true);
    const { error } = await supabase.from('event_attendance')
      .insert({ event_id: eventId, voter_id: voter.id, support_before: voter.support || null });
    if (error) { alert('Check-in failed: ' + error.message); setAttendeePending(false); return; }
    const next = [...list, { voter_id: voter.id, support_before: voter.support || null }];
    setAttendeesByEvent(prev => ({ ...prev, [eventId]: next }));
    setAttendeeSearch('');
    await syncCount(eventId, next.length);
    setAttendeePending(false);
  };
  const removeAttendee = async (eventId, voterId) => {
    setAttendeePending(true);
    const { error } = await supabase.from('event_attendance').delete()
      .eq('event_id', eventId).eq('voter_id', voterId);
    if (error) { alert('Remove failed: ' + error.message); setAttendeePending(false); return; }
    const next = (attendeesByEvent[eventId] || []).filter(a => a.voter_id !== voterId);
    setAttendeesByEvent(prev => ({ ...prev, [eventId]: next }));
    await syncCount(eventId, next.length);
    setAttendeePending(false);
  };

  const startEdit = (event) => {
    setEditingId(event.id);
    setEditForm({
      name: event.name || '',
      type: event.type || 'Rally / Public Meeting',
      date: event.date || '',
      time: event.time || '',
      island: event.island || '',
      location: event.location || '',
      expected_attendance: event.expected_attendance || '',
      cost: event.cost || '',
      notes: event.notes || ''
    });
  };

  const getStatusColor = (event) => {
    if (event.completed) return '#10b981';
    const days = daysUntil(event.date);
    if (days < 0) return 'var(--text-3)';
    if (days === 0) return '#10b981';
    if (days <= 7) return '#f59e0b';
    return '#3b82f6';
  };

  const getStatusLabel = (event) => {
    if (event.completed) return 'Completed';
    const days = daysUntil(event.date);
    if (days < 0) return 'Past';
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `In ${days} days`;
  };

  return (
    <div>
      {/* Header */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderRadius: '14px', padding: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <span style={{ width: 38, height: 38, borderRadius: 11, display: 'grid', placeItems: 'center', background: '#f59e0b1f', color: '#f59e0b', flexShrink: 0 }}>
              <Icon name="calendar" size={20} />
            </span>
            <div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 2 }}>Campaign</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', fontWeight: 600, margin: 0, color: 'var(--text-strong)', letterSpacing: '-0.3px', lineHeight: 1 }}>Events</h2>
            </div>
          </div>
          <button onClick={() => setShowAdd(!showAdd)} style={{
            padding: '10px 18px', borderRadius: '9px', border: 'none',
            background: showAdd ? 'var(--border-2)' : '#3b82f6', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
            fontFamily: "'DM Mono', monospace", display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'filter 0.15s'
          }}
            onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.12)'}
            onMouseLeave={e => e.currentTarget.style.filter = 'none'}
          >{showAdd ? 'Cancel' : <><Icon name="plus" size={15} color="#fff" /> Create Event</>}</button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          {[
            { value: stats.total,      label: 'Total Events', color: '#3b82f6', filter: 'All',      icon: 'calendar' },
            { value: stats.upcoming,   label: 'Upcoming',     color: '#10b981', filter: 'Upcoming', icon: 'clock' },
            { value: stats.attendance, label: 'Attendance',   color: '#f59e0b', filter: null,       icon: 'users' },
            { value: `MVR ${stats.cost.toLocaleString()}`, label: 'Total Cost', color: '#8b5cf6', filter: null, icon: 'coins' },
          ].map(({ value, label, color, filter, icon }) => {
            const on = filter && filterView === filter;
            return (
              <div key={label} onClick={filter ? () => setFilterView(filter) : undefined}
                style={{
                  background: 'var(--surface-2)', padding: '14px 16px', borderRadius: '12px',
                  minHeight: 96, position: 'relative', overflow: 'hidden',
                  cursor: filter ? 'pointer' : 'default',
                  border: on ? `1px solid ${color}` : '1px solid var(--border)',
                  boxShadow: on ? `0 0 0 1px ${color}` : 'none',
                  transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
                }}
                onMouseEnter={e => { if (filter) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = color; } }}
                onMouseLeave={e => { if (filter) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = on ? color : 'var(--border)'; } }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-2)', fontWeight: 500, lineHeight: 1.2 }}>{label}</span>
                  <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', background: color + '1f', color, flexShrink: 0 }}>
                    <Icon name={icon} size={15} />
                  </span>
                </div>
                <div style={{ fontSize: typeof value === 'string' ? '18px' : '28px', fontWeight: '700', color, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.5px' }}>{value}</div>
                {filter && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: color, opacity: on ? 1 : 0, transition: 'opacity 0.15s' }} />}
              </div>
            );
          })}
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {['All', 'Upcoming', 'Overdue', 'Completed'].map(view => {
            const on = filterView === view;
            return (
              <button key={view} onClick={() => setFilterView(view)} style={{
                padding: '8px 16px', borderRadius: '9px',
                border: on ? '1px solid rgba(59,130,246,0.55)' : '1px solid var(--border)',
                background: on ? 'rgba(59,130,246,0.13)' : 'transparent',
                color: on ? '#60a5fa' : 'var(--text-2)',
                fontSize: '12.5px', fontWeight: '600', cursor: 'pointer',
                fontFamily: "'DM Mono', monospace",
                transition: 'background 0.15s, border-color 0.15s, color 0.15s',
              }}
                onMouseEnter={e => { if (!on) { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text)'; } }}
                onMouseLeave={e => { if (!on) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; } }}
              >{view}</button>
            );
          })}
        </div>
      </div>

      {/* Add Event Form */}
      {showAdd && (
        <div style={{ background: 'var(--surface)', borderRadius: '14px', padding: '20px', marginBottom: '20px', border: '1px solid rgba(59,130,246,0.5)', boxShadow: 'var(--shadow-md)' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-strong)' }}>Create New Event</h3>
          <EventForm form={addForm} setForm={setAddForm} onSubmit={handleAddEvent} onCancel={() => { setShowAdd(false); setAddForm(BLANK_FORM); }} submitLabel="Create Event" allLocations={allLocations} allVoters={allVoters} />
        </div>
      )}

      {/* Events List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-2)' }}>
          <div style={{
            width: '40px', height: '40px', border: '3px solid var(--border-2)',
            borderTopColor: '#3b82f6', borderRadius: '50%',
            animation: 'spin 1s linear infinite', margin: '0 auto 16px'
          }} />
          Loading events...
        </div>
      ) : (() => {
        // Group events by island. Events without an island go under "__no_island__".
        const grouped = {};
        for (const ev of events) {
          const key = ev.island || '__no_island__';
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(ev);
        }
        const islands = Object.keys(grouped).filter(k => k !== '__no_island__').sort();
        const sections = [...islands, ...(grouped['__no_island__'] ? ['__no_island__'] : [])];

        const renderEventCard = (event) => {
            const isPast = daysUntil(event.date) < 0;
            const color = getStatusColor(event);
            const isEditing = editingId === event.id;
            const isPending = pendingId === event.id;

            return (
              <div key={event.id} style={{
                background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderRadius: '12px', padding: '20px',
                borderLeft: `4px solid ${color}`
              }}>
                {isEditing ? (
                  <>
                    <div style={{ marginBottom: '12px', fontWeight: '600', color: 'var(--text-2)' }}>Editing: {event.name}</div>
                    <EventForm form={editForm} setForm={setEditForm}
                      onSubmit={() => handleSaveEdit(event.id)}
                      submitLabel={isPending ? 'Saving…' : 'Save Changes'}
                      disabled={isPending}
                      onCancel={() => setEditingId(null)}
                      allLocations={allLocations}
                      allVoters={allVoters}
                    />
                  </>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-strong)' }}>{event.name}</h4>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: '#3b82f620', color: '#3b82f6' }}>{event.type}</span>
                        <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: color + '20', color }}>{getStatusLabel(event)}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: '13px', color: 'var(--text-2)' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon name="calendar" size={14} color="var(--text-4)" /> {formatDate(event.date)}{event.time ? ` at ${event.time}` : ''}</span>
                        {(event.island || event.location) && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon name="mapPin" size={14} color="var(--text-4)" /> {[event.island, event.location].filter(Boolean).join(' · ')}</span>
                        )}
                        {event.expected_attendance > 0 && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon name="users" size={14} color="var(--text-4)" /> Expected: {event.expected_attendance}{event.actual_attendance > 0 ? ` · Actual: ${event.actual_attendance}` : ''}</span>
                        )}
                        {event.cost > 0 && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon name="coins" size={14} color="var(--text-4)" /> MVR {Number(event.cost).toLocaleString()}</span>
                        )}
                      </div>
                      {event.notes && <p style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text)' }}>{event.notes}</p>}

                      {/* Attendance entry for past events */}
                      {isPast && (
                        <div style={{ marginTop: '12px' }}>
                          {attendanceId === event.id ? (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <input
                                type="number" value={attendanceValue}
                                onChange={e => setAttendanceValue(e.target.value)}
                                placeholder="Actual attendance"
                                style={{ ...inputStyle, width: '180px' }}
                              />
                              <button onClick={() => handleSaveAttendance(event.id)} disabled={isPending}
                                style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', background: '#10b981', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                                {isPending ? '…' : 'Save'}
                              </button>
                              <button onClick={() => { setAttendanceId(null); setAttendanceValue(''); }}
                                style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontSize: '13px', cursor: 'pointer' }}>
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button onClick={() => { setAttendanceId(event.id); setAttendanceValue(event.actual_attendance || ''); }}
                              style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontSize: '13px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              {event.actual_attendance > 0 ? <><Icon name="edit" size={13} /> Update attendance ({event.actual_attendance})</> : <><Icon name="users" size={13} /> Record attendance</>}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Attendee check-in (links real voters via event_attendance) */}
                      <div style={{ marginTop: 12 }}>
                        {(() => {
                          const list = attendeesByEvent[event.id];
                          const count = list ? list.length : (event.actual_attendance || 0);
                          const isOpen = openAttendeesId === event.id;
                          return (
                            <>
                              <button onClick={() => toggleAttendees(event.id)}
                                style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-2)', background: isOpen ? 'var(--surface-2)' : 'transparent', color: 'var(--text-2)', fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <Icon name="users" size={13} /> Attendees ({count}) <Icon name={isOpen ? 'chevronDown' : 'chevronRight'} size={12} />
                              </button>
                              {isOpen && (
                                <div style={{ marginTop: 10, padding: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10 }}>
                                  {list && list.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                                      {list.map(a => {
                                        const v = voterById.get(a.voter_id);
                                        return (
                                          <span key={a.voter_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '3px 10px', fontSize: 12 }}>
                                            <Icon name="check" size={12} color="#10b981" />
                                            <span style={{ color: 'var(--text)' }}>{v ? v.name : `Voter #${a.voter_id}`}</span>
                                            <button type="button" onClick={() => removeAttendee(event.id, a.voter_id)} disabled={attendeePending}
                                              style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', fontSize: 14, padding: '0 0 0 2px' }}>×</button>
                                          </span>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 10 }}>{list ? 'No one checked in yet.' : 'Loading…'}</div>
                                  )}
                                  <div style={{ position: 'relative' }}>
                                    <input value={attendeeSearch} onChange={e => setAttendeeSearch(e.target.value)}
                                      placeholder="Search voters to check in…" style={{ ...inputStyle }} />
                                    {attendeeSearch.trim().length >= 2 && (() => {
                                      const q = attendeeSearch.toLowerCase();
                                      const taken = new Set((list || []).map(a => a.voter_id));
                                      const results = (allVoters || []).filter(v => !taken.has(v.id) && v.name?.toLowerCase().includes(q)).slice(0, 8);
                                      return results.length > 0 ? (
                                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, zIndex: 10, maxHeight: 220, overflowY: 'auto', marginTop: 2 }}>
                                          {results.map(v => (
                                            <div key={v.id} onClick={() => checkInVoter(event.id, v)}
                                              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 13 }}
                                              onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                              <span style={{ color: 'var(--text-strong)' }}>{v.name}</span>
                                              {(v.island || v.ward) && <span style={{ color: 'var(--text-3)', marginLeft: 8, fontSize: 11 }}>{v.island}{v.ward ? ` · ${v.ward}` : ''}</span>}
                                            </div>
                                          ))}
                                        </div>
                                      ) : null;
                                    })()}
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '90px' }}>
                      {!event.completed && (
                        <button onClick={() => handleCompleteEvent(event.id)} disabled={isPending}
                          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: isPending ? 'var(--border-2)' : '#10b981', color: 'white', fontSize: '13px', fontWeight: '600', cursor: isPending ? 'not-allowed' : 'pointer', opacity: isPending ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          {isPending ? '…' : <><Icon name="check" size={14} color="#fff" /> Complete</>}
                        </button>
                      )}
                      <button onClick={() => startEdit(event)} disabled={isPending}
                        style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                        Edit
                      </button>
                      <button onClick={() => handleDeleteEvent(event.id)} disabled={isPending}
                        style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--border-2)', background: 'transparent', color: isPending ? 'var(--text-3)' : '#ef4444', fontSize: '13px', fontWeight: '600', cursor: isPending ? 'not-allowed' : 'pointer', opacity: isPending ? 0.6 : 1 }}>
                        {isPending ? '…' : 'Delete'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
        };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {sections.map(key => {
              const isNoIsland = key === '__no_island__';
              const isCollapsed = !expandedIslands.has(key);
              const headColor = isNoIsland ? '#ef4444' : 'var(--link)';
              return (
                <div key={key}>
                  <div onClick={() => toggleIsland(key)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isCollapsed ? 0 : 10, cursor: 'pointer', userSelect: 'none' }}>
                    <Icon name="mapPin" size={13} color={headColor} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: headColor, textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: "'DM Mono', monospace" }}>
                      {isNoIsland ? 'Unspecified Island' : key}
                    </span>
                    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                    <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{grouped[key].length} event{grouped[key].length !== 1 ? 's' : ''}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-4)', marginLeft: 4 }}>{isCollapsed ? '▸' : '▾'}</span>
                  </div>
                  {!isCollapsed && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {grouped[key].map(renderEventCard)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {!loading && events.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-3)' }}>
          <div style={{ display: 'grid', placeItems: 'center', marginBottom: '14px' }}><Icon name="calendar" size={42} color="var(--text-4)" /></div>
          <p>No {filterView.toLowerCase()} events</p>
          {filterView === 'Upcoming' && <p style={{ fontSize: '13px', marginTop: '8px' }}>Click "+ Create Event" to add one</p>}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function EventForm({ form, setForm, onSubmit, submitLabel, disabled, onCancel, allLocations = [], allVoters = [] }) {
  // Only show islands that actually have voters in this campaign. Falls back
  // to the locations table for islands you might want before voters are added.
  const locationOptions = (() => {
    const fromVoters = (allVoters || []).map(v => v.island).filter(Boolean);
    const fromDb = (allLocations || []).map(l => l.island).filter(Boolean);
    return [...new Set([...fromVoters, ...fromDb])].sort();
  })();
  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '6px',
    border: '1px solid var(--border-2)', background: 'var(--surface-2)', color: 'var(--text)', fontSize: '14px'
  };
  const label = (text) => (
    <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-2)' }}>{text}</label>
  );

  return (
    <div style={{ display: 'grid', gap: '16px' }}>
      <div>
        {label('Event Name')}
        <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
        <div>
          {label('Type')}
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={inputStyle}>
            {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          {label('Island')}
          <SearchableSelect
            value={form.island}
            options={locationOptions}
            onChange={v => setForm({ ...form, island: v || '' })}
            placeholder="Select island…"
            onAddNew={(name) => {
              const v = name.trim();
              setForm({ ...form, island: v });
              return v;
            }}
            addNewLabel="Add"
          />
        </div>
      </div>

      <div>
        {label('Location / Venue')}
        <input type="text" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} style={inputStyle} placeholder="e.g., Community Hall, School Grounds" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
        <div>
          {label('Date')}
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} style={inputStyle} />
        </div>
        <div>
          {label('Time')}
          <input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} style={inputStyle} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
        <div>
          {label('Expected Attendance')}
          <input type="number" value={form.expected_attendance} onChange={e => setForm({ ...form, expected_attendance: e.target.value })} style={inputStyle} />
        </div>
        <div>
          {label('Cost (MVR)')}
          <input type="number" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} style={inputStyle} />
        </div>
      </div>

      <div>
        {label('Notes')}
        <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
          style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        <button onClick={onSubmit} disabled={disabled} style={{
          flex: 1, padding: '12px', borderRadius: '8px', border: 'none',
          background: disabled ? 'var(--border-2)' : '#10b981', color: 'white',
          fontSize: '14px', fontWeight: '600', cursor: disabled ? 'not-allowed' : 'pointer'
        }}>{submitLabel}</button>
        {onCancel && (
          <button onClick={onCancel} style={{
            padding: '12px 20px', borderRadius: '8px', border: '1px solid var(--border-2)',
            background: 'transparent', color: 'var(--text-2)', fontSize: '14px', cursor: 'pointer'
          }}>Cancel</button>
        )}
      </div>
    </div>
  );
}
